<template>
<div>
    <card v-if="upload_response !== null" class="mb-3" scroll-on-show="true">
        <template slot="header">
            <strong>Import Complete</strong>
        </template>

        <template slot="body">
            <div class="flex jc-sb ai-fs">
                <div class="flex-50">
                    <p>
                        <span class="mr-2 primary-text">
                            <i class="material-icons">check</i>
                        </span>
                        {{ upload_response.insert_count }} tenants were successfully imported.
                    </p>

                    <p v-if="upload_response.active_tenants_count !== 0">
                        <span class="mr-2 warning-text">
                            <i class="material-icons">warning</i>
                        </span>
                        {{ upload_response.active_tenants_count }} tenants are already active in the system.
                    </p>

                    <p v-if="upload_response.error_count !== 0">
                        <span class="mr-2 danger-text">
                            <i class="material-icons">warning</i>
                        </span>
                        {{ upload_response.error_count }} tenants had a Prime Site ID not in the system.
                    </p>

                    <p v-if="upload_response.duplicate_tenant_filename">
                        <span class="mr-2 warning-text">
                            <i class="material-icons">warning</i>
                        </span>

                        {{ upload_response.duplicate_tenant_count }} tenants have already gone through the system. <a :href="`${api_base_path}/api/exports/download?file_key=${upload_response.duplicate_tenant_filename}&filename=duplicate_tenants`" target="_blank">Download list</a>
                    </p>
                </div>

                <div class="flex-50">
                    <div v-if="upload_response.missing_prime_ids.length !== 0">
                        <p>The following Prime Site IDs were not found, therefore their tenants were not imported.</p>

                        <div class="flex jc-sb fw-w">
                            <div v-for="id in upload_response.missing_prime_ids" :key="id" class="missing-prime-id flex-25">
                                <span class="missing-prime-id--inner">{{ id }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </card>

    <card>
        <template slot="header">
            <i class="material-icons">cloud_upload</i>
            <strong class="ml-2">Import Tenants</strong>
        </template>

        <template slot="body">
            <div class="flex ai-c jc-sb">
                <label class="flex-0 ws-nw mr-3">Software Type:</label>
                <select-input class="flex-a"
                    :active-index="software_input_data.active_index"
                    :choices="software_input_data.choices"
                    @change="onChangeSoftware"></select-input>
            </div>

            <div class="mt-6">
                <label>Rent Roll (must be a .xlsx file)</label>
                <file-upload @change="(f) => files.rent_roll = f"></file-upload>

                <div v-show="active_software_choice.key === 'easystorage'">
                    <label class="mt-5">Tenant Address List (must be a .xlsx file)</label>
                    <file-upload @change="(f) => files.tenant_address_list = f"></file-upload>
                    <label class="mt-5">Insurance Sheet (must be a .xlsx file)</label>
                    <file-upload @change="(f) => files.insurance_sheet = f"></file-upload>
                </div>
            </div>

            <div class="flex ai-c jc-sb mt-6">
                <span>Note: If a file has multiple sheets, only the first sheet will be used.</span>

                <div class="flex jc-fe">
                    <div v-show="is_loading_upload" class="flex ai-c mr-2">
                        <img src="../assets/img/loading-black-32.gif" alt="loading" />
                    </div>

                    <button :disabled="upload_is_disabled" type="button" @click="onUpload">Upload</button>
                </div>
            </div>
        </template>
    </card>
</div>
</template>

<script>
import http from "@/http"

export default {
    name: "upload-tenants-page",

    data () {
        return {
            files: {
                insurance_sheet: null,
                rent_roll: null,
                tenant_address_list: null
            },
            is_loading_upload: false,
            software_input_data: {
                active_index: 0,
                choices: [
                    { key: "sitelink_web", label: "SiteLink - Web", badge: null },
                    { key: "syrasoft", label: "Syrasoft", badge: null },
                    { key: "storedge", label: "Storedge", badge: null },
                    { key: "easystorage", label: "EasyStorage", badge: null }
                ]
            },
            upload_response: null
        }
    },

    computed: {
        /**
         * Return the full software choice object for the active index.
         *
         * @return object
         */
        active_software_choice () {
            return this.software_input_data.choices[this.software_input_data.active_index]
        },

        api_base_path () {
            return http.defaults.baseURL
        },

        /**
         * Return true if the upload function should be disabled.
         *
         * @return boolean
         */
        upload_is_disabled () {
            if (this.is_loading_upload) {
                return true
            }

            switch (this.active_software_choice.key) {
            case "syrasoft":
                return !this.files.rent_roll
            case "sitelink_web":
                return !this.files.rent_roll
            case "storedge":
                return !this.files.rent_roll
            case "easystorage":
                return !(this.files.rent_roll && this.files.tenant_address_list && this.files.insurance_sheet)
            }
        }
    },

    methods: {
        /**
         * When the software dropdown is changed, set the active option index.
         *
         * @param int index Selected option index
         */
        onChangeSoftware (index) {
            this.software_input_data.active_index = index
        },

        /**
         * Upload the file to the server so the records can be added.
         */
        async onUpload () {
            const params = new FormData()

            this.is_loading_upload = true
            this.result_counts = {}

            params.append("software", this.active_software_choice.key)

            switch (this.active_software_choice.key) {
            case "syrasoft":
                params.append("rent_roll", this.files.rent_roll)
                break
            case "sitelink_web":
                params.append("rent_roll", this.files.rent_roll)
                break
            case "storedge":
                params.append("rent_roll", this.files.rent_roll)
                break
            case "easystorage":
                params.append("rent_roll", this.files.rent_roll)
                params.append("tenant_address_list", this.files.tenant_address_list)
                params.append("insurance_sheet", this.files.insurance_sheet)
                break
            default:
                this.$modalManager.add({ state: "bad", header: "Error", body: `No such software as '${this.active_software_choice.label}'!` })
                return
            }

            this.upload_response = null

            try {
                const { data: response } = await http.post("/api/tenants/import", params)

                if (response === undefined) {
                    this.$modalManager.add({ state: "bad", header: "Error", body: "An unknown error occurred!" })
                } else {
                    this.upload_response = response

                    if (response.duplicate_tenant_filename) {
                        const now = new Date()
                        const datestamp = `${now.getFullYear()}-${("0" + (now.getMonth() + 1)).slice(-2)}-${("0" + now.getDate()).slice(-2)}`

                        const url = `${this.api_base_path}/api/exports/download?file_key=${response.duplicate_tenant_filename}&filename=duplicate_tenants_${datestamp}`

                        window.open(url, "_blank")
                    }
                }
            } catch (ex) {
                this.$modalManager.add({ state: "bad", header: "Error", body: ex })
            } finally {
                this.is_loading_upload = false
            }
        }
    }
}
</script>

<style scoped>
.missing-prime-id {
    flex-grow: 0;
    text-align: center;
}

.missing-prime-id:nth-child(n + 5) .missing-prime-id--inner {
    margin-top: 10px;
}

.missing-prime-id:nth-child(n+1):not(:nth-child(4n+4)) {
    padding-right: 5px;
}

.missing-prime-id:nth-child(n+1):not(:nth-child(4n+1)) {
    padding-left: 5px;
}

.missing-prime-id--inner {
    background: #f0f0f0;
    border: 1px solid #b5b5b5;
    display: block;
    line-height: 1;
    padding: 8px 12px;
}
</style>



// WEBPACK FOOTER //
// src/pages/UploadTenantsPage.vue